import {
  Avatar,
  Chip,
  DataTable,
  type DataTableColumnDef,
} from "@assetwatch/ui";
import type { DocumentListRow } from "../../../types/documents";

interface DocumentsListProps {
  rows: DocumentListRow[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRowClick?: (documentId: number) => void;
}

type CategoryTone =
  | "informative"
  | "brand"
  | "negative"
  | "warning"
  | "neutral";

type DocumentCellInfo = {
  getValue: () => unknown;
  row: { original: DocumentListRow };
};

const getCategoryTone = (categoryName?: string): CategoryTone => {
  if (!categoryName) return "neutral";
  switch (categoryName.toLowerCase()) {
    case "manual":
      return "informative";
    case "procedure":
      return "brand";
    case "health & safety":
      return "negative";
    case "drawings":
      return "brand";
    case "qualifications":
      return "warning";
    default:
      return "neutral";
  }
};

export const DocumentsList = ({
  rows,
  currentPage,
  totalPages,
  onPageChange,
  onRowClick,
}: DocumentsListProps) => {
  const columns: DataTableColumnDef<DocumentListRow>[] = [
    {
      accessorFn: (row: DocumentListRow) => row.documentName,
      id: "documentName",
      header: "Document",
      cell: (info: DocumentCellInfo) => {
        const row = info.row.original;
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (onRowClick) {
                onRowClick(row.documentId);
              }
            }}
            className="flex flex-col gap-1 text-left hover:underline"
          >
            <span className="font-medium text-link">{row.documentName}</span>
            <span className="text-xs text-muted">{row.documentNumber}</span>
          </button>
        );
      },
      meta: { width: "250px" },
    },
    {
      accessorFn: (row: DocumentListRow) =>
        row.primaryCategory?.categoryName || "",
      id: "category",
      header: "Category",
      cell: (info: DocumentCellInfo) => {
        const row = info.row.original;
        return row.primaryCategory ? (
          <Chip
            tone={getCategoryTone(row.primaryCategory.categoryName)}
            label={row.primaryCategory.categoryName}
          />
        ) : (
          <span className="text-muted">—</span>
        );
      },
      meta: { width: "150px" },
    },
    {
      accessorFn: (row: DocumentListRow) => row.status,
      id: "status",
      header: "Status",
      cell: (info: DocumentCellInfo) => {
        const row = info.row.original;
        return (
          <Chip
            tone={row.status === "Published" ? "positive" : "neutral"}
            label={row.status}
          />
        );
      },
      meta: { width: "120px" },
    },
    {
      accessorFn: (row: DocumentListRow) => row.fileExtension,
      id: "fileExtension",
      header: "Type",
      cell: (info: DocumentCellInfo) => (
        <span className="text-sm">{String(info.getValue())}</span>
      ),
      meta: { width: "80px" },
    },
    {
      accessorFn: (row: DocumentListRow) => row.fileSize,
      id: "fileSize",
      header: "Size",
      cell: (info: DocumentCellInfo) => (
        <span className="text-sm">
          {formatFileSize(Number(info.getValue()))}
        </span>
      ),
      meta: { width: "100px" },
    },
    {
      accessorFn: (row: DocumentListRow) => row.owner.name,
      id: "owner",
      header: "Owner",
      cell: (info: DocumentCellInfo) => {
        const row = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <Avatar
              size="xs"
              variant="internal"
              initials={row.owner.initials}
            />
            <span className="text-sm">{row.owner.name}</span>
          </div>
        );
      },
      meta: { width: "150px" },
    },
    {
      accessorFn: (row: DocumentListRow) => row.uploadedDate,
      id: "uploadedDate",
      header: "Uploaded",
      cell: (info: DocumentCellInfo) => (
        <span className="text-sm">{String(info.getValue())}</span>
      ),
      meta: { width: "120px" },
    },
    {
      accessorFn: (row: DocumentListRow) => row.lastUpdatedDate,
      id: "lastUpdatedDate",
      header: "Last Updated",
      cell: (info: DocumentCellInfo) => (
        <span className="text-sm">{String(info.getValue())}</span>
      ),
      meta: { width: "150px" },
    },
    {
      accessorFn: (row: DocumentListRow) => row.associationCount,
      id: "associationCount",
      header: "Associations",
      cell: (info: DocumentCellInfo) => {
        const count = Number(info.getValue());
        return count > 0 ? (
          <span className="text-sm font-medium">
            {count} asset{count !== 1 ? "s" : ""}
          </span>
        ) : (
          <span className="text-muted">—</span>
        );
      },
      meta: { width: "120px" },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <DataTable
        ariaLabel="Documents"
        columns={columns}
        data={rows}
        getRowId={(row: DocumentListRow) => String(row.documentId)}
      />
      {/* Custom Pagination */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted">
          Page {currentPage + 1} of {totalPages}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={currentPage === 0}
            onClick={() => onPageChange(currentPage - 1)}
            className="rounded border border-border px-3 py-1 text-sm disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={currentPage >= totalPages - 1}
            onClick={() => onPageChange(currentPage + 1)}
            className="rounded border border-border px-3 py-1 text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / k ** i) * 10) / 10} ${sizes[i]}`;
}
